"use client";

/**
 * Canvas performance instrumentation (画布性能埋点)
 * ------------------------------------------------------------------
 * 设计目标：
 * - 生产环境零开销：所有热路径调用都应包在 `perfEnabled()` 守卫内；关闭时这是一个
 *   读模块级布尔值的内联调用，JIT 友好，可忽略不计。
 * - 一键开关：URL `?perf=1`、localStorage、或运行时调用 `setPerfEnabled(true)`。
 *   亦可在控制台用 `window.__canvasPerf.enable()` / `.disable()`。
 * - 自描述：聚合 计数器 / 计时器(min/max/avg/p95) / 渲染计数 / FPS / 仪表盘(gauge) /
 *   最近事件环形缓冲，供 HUD 浮层与控制台 `window.__canvasPerf.dump()` 使用。
 *
 * 用法速览：
 *   if (perfEnabled()) perf.count("nodeChange:position");
 *   const stop = perf.startTimer("dragFlush"); ...; stop();
 *   perf.gauge("nodes", nodes.length);
 *   perf.recordRender("ImageNode");
 *   perf.frame();            // 每个 rAF 调一次以统计 FPS
 *   perf.event("save", "commit", { ms, result });
 */

const STORAGE_KEY = "flowmuse:perf-debug";
const EVENT_RING_SIZE = 120;
const TIMER_SAMPLE_SIZE = 240;
const FRAME_WINDOW_MS = 1000;

type TimerStat = {
  count: number;
  total: number;
  min: number;
  max: number;
  last: number;
  /** 环形采样，用于近似 p95 */
  samples: number[];
  sampleHead: number;
};

type PerfEvent = {
  t: number;
  group: string;
  name: string;
  data?: Record<string, unknown>;
};

type PerfState = {
  enabled: boolean;
  /**
   * 调试模式等级：
   * - 0：关闭
   * - 1：性能监测（HUD + 埋点），对应 `?perf=1`
   * - 2：节点检查器（悬浮窗实时显示选中节点数据），对应 `?perf=2`
   */
  level: number;
  startedAt: number;
  counters: Map<string, number>;
  timers: Map<string, TimerStat>;
  renders: Map<string, { count: number; lastT: number }>;
  /** 重渲染归因：组件 → (变化原因 key → 次数)。用于回答“为什么重复渲染”。 */
  renderReasons: Map<string, Map<string, number>>;
  gauges: Map<string, number>;
  /** FPS 计算：保留最近 1s 内的帧时间戳 */
  frameTimes: number[];
  /** 最近一帧间隔（ms），用于侦测长帧/掉帧 */
  lastFrameT: number;
  longFrames: number;
  events: PerfEvent[];
  eventHead: number;
};

export type PerfTimerSnapshot = {
  name: string;
  count: number;
  avg: number;
  min: number;
  max: number;
  last: number;
  p95: number;
};

export type PerfSnapshot = {
  enabled: boolean;
  uptimeMs: number;
  fps: number;
  longFrames: number;
  lastFrameMs: number;
  counters: Array<{ name: string; value: number }>;
  timers: PerfTimerSnapshot[];
  renders: Array<{ name: string; count: number }>;
  renderReasons: Array<{
    component: string;
    total: number;
    reasons: Array<{ key: string; count: number }>;
  }>;
  gauges: Array<{ name: string; value: number }>;
  recentEvents: PerfEvent[];
};

const now = (): number => {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
};

function createState(): PerfState {
  return {
    enabled: false,
    level: 0,
    startedAt: now(),
    counters: new Map(),
    timers: new Map(),
    renders: new Map(),
    renderReasons: new Map(),
    gauges: new Map(),
    frameTimes: [],
    lastFrameT: 0,
    longFrames: 0,
    events: [],
    eventHead: 0,
  };
}

const state: PerfState = createState();
const subscribers = new Set<() => void>();

function readInitialLevel(): number {
  if (typeof window === "undefined") return 0;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("perf");
    if (q === "1" || q === "true") return 1;
    if (q === "2") return 2;
    if (q === "0" || q === "false") return 0;
  } catch {}
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return 1;
    if (stored === "2") return 2;
  } catch {}
  return 0;
}

let initialized = false;
function ensureInit() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  state.level = readInitialLevel();
  state.enabled = state.level === 1;
  // 暴露控制台 API
  try {
    (window as any).__canvasPerf = {
      enable: () => setPerfEnabled(true),
      disable: () => setPerfEnabled(false),
      toggle: () => setPerfEnabled(!state.enabled),
      level: (n: number) => setPerfLevel(n),
      reset: resetPerf,
      snapshot: getPerfSnapshot,
      dump: dumpPerf,
      export: (label?: string) => exportPerf(label),
    };
  } catch {}
}

function notify() {
  for (const cb of subscribers) {
    try {
      cb();
    } catch {}
  }
}

/** 热路径守卫：关闭时仅读一个布尔值。仅性能监测（level 1）开启时为真。 */
export function perfEnabled(): boolean {
  if (!initialized) ensureInit();
  return state.enabled;
}

/** 当前调试模式等级（0 关闭 / 1 性能监测 / 2 节点检查器）。 */
export function perfLevel(): number {
  if (!initialized) ensureInit();
  return state.level;
}

export function setPerfLevel(level: number) {
  ensureInit();
  const next = level === 1 || level === 2 ? level : 0;
  state.level = next;
  state.enabled = next === 1;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? String(next) : "0");
  } catch {}
  if (state.enabled) {
    // 重置起点，避免历史数据干扰本次会话
    state.startedAt = now();
  }
  notify();
}

export function setPerfEnabled(enabled: boolean) {
  setPerfLevel(enabled ? 1 : 0);
}

export function resetPerf() {
  state.counters.clear();
  state.timers.clear();
  state.renders.clear();
  state.renderReasons.clear();
  state.gauges.clear();
  state.frameTimes.length = 0;
  state.lastFrameT = 0;
  state.longFrames = 0;
  state.events.length = 0;
  state.eventHead = 0;
  state.startedAt = now();
  notify();
}

function count(name: string, n = 1) {
  state.counters.set(name, (state.counters.get(name) ?? 0) + n);
}

function gauge(name: string, value: number) {
  state.gauges.set(name, value);
}

function recordTiming(name: string, ms: number) {
  let stat = state.timers.get(name);
  if (!stat) {
    stat = {
      count: 0,
      total: 0,
      min: Infinity,
      max: 0,
      last: 0,
      samples: [],
      sampleHead: 0,
    };
    state.timers.set(name, stat);
  }
  stat.count += 1;
  stat.total += ms;
  stat.last = ms;
  if (ms < stat.min) stat.min = ms;
  if (ms > stat.max) stat.max = ms;
  if (stat.samples.length < TIMER_SAMPLE_SIZE) {
    stat.samples.push(ms);
  } else {
    stat.samples[stat.sampleHead] = ms;
    stat.sampleHead = (stat.sampleHead + 1) % TIMER_SAMPLE_SIZE;
  }
}

/** 返回一个停止函数；调用后记录耗时。关闭时返回 no-op。 */
function startTimer(name: string): () => number {
  const t0 = now();
  return () => {
    const dt = now() - t0;
    recordTiming(name, dt);
    return dt;
  };
}

function recordRender(component: string) {
  let r = state.renders.get(component);
  if (!r) {
    r = { count: 0, lastT: 0 };
    state.renders.set(component, r);
  }
  r.count += 1;
  r.lastT = now();
}

/**
 * 记录一次重渲染的归因。`reasonKeys` 为本次相对上一次发生变化的字段名集合
 * （例如 ["data.generated"]）。空数组表示“无可见变化仍重渲染”（通常是 memo
 * 失效 / 父级强制刷新），记为 `__no-change__`，是排查重复渲染的关键信号。
 */
function recordRenderReason(component: string, reasonKeys: string[]) {
  let m = state.renderReasons.get(component);
  if (!m) {
    m = new Map();
    state.renderReasons.set(component, m);
  }
  if (reasonKeys.length === 0) {
    m.set("__no-change__", (m.get("__no-change__") ?? 0) + 1);
    return;
  }
  for (const k of reasonKeys) {
    m.set(k, (m.get(k) ?? 0) + 1);
  }
}

/** 每个 rAF 调一次以统计 FPS 与长帧。 */
function frame() {
  const t = now();
  if (state.lastFrameT > 0) {
    const dt = t - state.lastFrameT;
    // 超过 ~2 帧（>33ms）记为长帧（基于 60fps 基线）
    if (dt > 33) state.longFrames += 1;
  }
  state.lastFrameT = t;
  state.frameTimes.push(t);
  // 丢弃 1s 之外的帧
  const cutoff = t - FRAME_WINDOW_MS;
  let i = 0;
  while (i < state.frameTimes.length && state.frameTimes[i] < cutoff) i += 1;
  if (i > 0) state.frameTimes.splice(0, i);
}

function event(group: string, name: string, data?: Record<string, unknown>) {
  const ev: PerfEvent = { t: now(), group, name, data };
  if (state.events.length < EVENT_RING_SIZE) {
    state.events.push(ev);
  } else {
    state.events[state.eventHead] = ev;
    state.eventHead = (state.eventHead + 1) % EVENT_RING_SIZE;
  }
}

function percentile(samples: number[], p: number): number {
  if (samples.length === 0) return 0;
  const sorted = samples.slice().sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((p / 100) * sorted.length) - 1),
  );
  return sorted[idx];
}

export function getPerfSnapshot(): PerfSnapshot {
  const t = now();
  const fps = state.frameTimes.length; // 帧数 / 1s 窗口 ≈ FPS
  const lastFrameMs =
    state.frameTimes.length >= 2
      ? state.frameTimes[state.frameTimes.length - 1] -
        state.frameTimes[state.frameTimes.length - 2]
      : 0;

  const counters = Array.from(state.counters, ([name, value]) => ({
    name,
    value,
  })).sort((a, b) => b.value - a.value);

  const timers: PerfTimerSnapshot[] = Array.from(
    state.timers,
    ([name, s]) => ({
      name,
      count: s.count,
      avg: s.count ? s.total / s.count : 0,
      min: s.min === Infinity ? 0 : s.min,
      max: s.max,
      last: s.last,
      p95: percentile(s.samples, 95),
    }),
  ).sort((a, b) => b.avg - a.avg);

  const renders = Array.from(state.renders, ([name, r]) => ({
    name,
    count: r.count,
  })).sort((a, b) => b.count - a.count);

  const renderReasons = Array.from(state.renderReasons, ([component, m]) => {
    const reasons = Array.from(m, ([key, count]) => ({ key, count })).sort(
      (a, b) => b.count - a.count,
    );
    const total = reasons.reduce((sum, r) => sum + r.count, 0);
    return { component, total, reasons };
  }).sort((a, b) => b.total - a.total);

  const gauges = Array.from(state.gauges, ([name, value]) => ({
    name,
    value,
  }));

  // 还原事件环形缓冲的时间顺序
  const recentEvents =
    state.events.length < EVENT_RING_SIZE
      ? state.events.slice()
      : [
          ...state.events.slice(state.eventHead),
          ...state.events.slice(0, state.eventHead),
        ];

  return {
    enabled: state.enabled,
    uptimeMs: t - state.startedAt,
    fps,
    longFrames: state.longFrames,
    lastFrameMs,
    counters,
    timers,
    renders,
    renderReasons,
    gauges,
    recentEvents,
  };
}

export function dumpPerf() {
  const snap = getPerfSnapshot();
  try {
    // eslint-disable-next-line no-console
    console.group(
      `%c[canvas-perf] FPS=${snap.fps} longFrames=${snap.longFrames} uptime=${(
        snap.uptimeMs / 1000
      ).toFixed(1)}s`,
      "color:#22d3ee;font-weight:bold",
    );
    // eslint-disable-next-line no-console
    console.table(snap.timers);
    // eslint-disable-next-line no-console
    console.table(snap.counters);
    // eslint-disable-next-line no-console
    console.table(snap.renders);
    // 重渲染归因：每个组件展开“原因 → 次数”，直接定位重复渲染来源。
    for (const rr of snap.renderReasons) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `%c${rr.component} re-rendered ${rr.total}×`,
        "color:#f59e0b",
      );
      // eslint-disable-next-line no-console
      console.table(rr.reasons);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
    // eslint-disable-next-line no-console
    console.table(snap.gauges);
    // eslint-disable-next-line no-console
    console.groupEnd();
  } catch {}
  return snap;
}

/**
 * 导出当前快照为 JSON。`download !== false` 时触发浏览器下载，便于优化前后对比；
 * 同时把 JSON 文本复制到剪贴板（best-effort）并返回该文本。
 * `label` 会写入文件，用于标记“优化前/后”等基线名。
 */
export function exportPerf(label?: string, download = true): string {
  const snap = getPerfSnapshot();
  const payload = {
    label: label ?? null,
    capturedAt: new Date().toISOString(),
    url: typeof window !== "undefined" ? window.location.href : null,
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : null,
    snapshot: snap,
  };
  const json = JSON.stringify(payload, null, 2);
  try {
    navigator.clipboard?.writeText(json).catch(() => {});
  } catch {}
  if (download && typeof window !== "undefined") {
    try {
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeLabel = (label ?? "snapshot").replace(/[^\w.-]+/g, "-");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `canvas-perf-${safeLabel}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {}
  }
  return json;
}

export function subscribePerf(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

const noopTimer = () => 0;

/**
 * 统一埋点入口。每个方法在关闭时都是廉价 no-op（仍建议用 `perfEnabled()` 守卫
 * 包裹热路径以避免参数对象/字符串拼接的构造开销）。
 */
export const perf = {
  enabled: perfEnabled,
  count: (name: string, n = 1) => {
    if (state.enabled) count(name, n);
  },
  gauge: (name: string, value: number) => {
    if (state.enabled) gauge(name, value);
  },
  timing: (name: string, ms: number) => {
    if (state.enabled) recordTiming(name, ms);
  },
  startTimer: (name: string): (() => number) => {
    if (!state.enabled) return noopTimer;
    return startTimer(name);
  },
  recordRender: (component: string) => {
    if (state.enabled) recordRender(component);
  },
  recordRenderReason: (component: string, reasonKeys: string[]) => {
    if (state.enabled) recordRenderReason(component, reasonKeys);
  },
  frame: () => {
    if (state.enabled) frame();
  },
  event: (group: string, name: string, data?: Record<string, unknown>) => {
    if (state.enabled) event(group, name, data);
  },
};
